import { EditNotificationsOutlined } from "@mui/icons-material";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { DataGrid, GridDeleteIcon } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Header from "../../components/Header";
import ProjectModuleApi from "../../services/api/ProjectModules";
import { tokens } from "../../theme";
import ProjectModuleForm from "./form";
import ProjectApi from "../../services/api/projects";
import ModuleCategoryApi from "../../services/api/moduleCategories";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import useMediaQuery from "hooks/useMediaQuery";

const ProjectModules = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const projectModuleApi = ProjectModuleApi();
  const projectApi = ProjectApi();
  const moduleCategoryApi = ModuleCategoryApi();

  const [entitiesList, setEntitiesList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [moduleCategories, setModuleCategories] = useState([]);

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [editingEntity, setEditingEntity] = useState();

  const [open, setOpen] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width: 767px)");

  const columns = [
    { field: "id", headerName: "ID" },
    {
      field: "projectName",
      headerName: "Project",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "categoryName",
      headerName: "Category",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "path",
      headerName: "Path",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "order_no",
      headerName: "Order Number",
      width: 150,
      cellClassName: "name-column--cell",
    },
    {
      field: "",
      headerName: "Action",
      width: 200,
      renderCell: ({ row: { id } }) => {
        return (
          <Box
            display="flex"
            gap={2}
            justifyContent="center"
            borderRadius="4px"
          >
            <Box display="flex">
              <EditOutlinedIcon
                fontSize="small"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="warning"
                onClick={() => editEntity(id)}
                style={{ cursor: "pointer", marginRight: "10px" }}
              >
                Edit
              </Typography>
            </Box>

            <Box display="flex">
              <DeleteOutlineOutlinedIcon
                fontSize="small"
                color="error"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="error"
                onClick={() => deleteEntity(id)}
                style={{ cursor: "pointer" }}
              >
                Delete
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchProjects();
    fetchModuleCategories();
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const data = await projectModuleApi.getAll();

      setEntitiesList(data.results);
    } catch (error) {
      console.log(fetchErrorMsg, error);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectApi.getAll();

      setProjects(data.results);
    } catch (error) {
      console.log(fetchErrorMsg, error);
    }
  };

  const fetchModuleCategories = async () => {
    try {
      const data = await moduleCategoryApi.getAll();

      setModuleCategories(data.results);
    } catch (error) {
      console.log(fetchErrorMsg, error);
    }
  };

  const editEntity = (id) => {
    setEditingEntity(entitiesList.find((row) => row.id === id));
    viewForm();
  };

  const deleteEntity = (id) => {
    setSelectedId(id);
    handleOpenConfirmation();
  };

  const handleOpenConfirmation = () => {
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const handleConfirmAction = () => {
    try {
      const response = projectModuleApi.destroy(selectedId);

      console.log(deletedSuccessMsg, response);
      fetchEntities();
    } catch (error) {
      console.log(deleteErrorMsg, error);
    }

    handleCloseConfirmation();
  };

  const openCreateForm = () => {
    setEditingEntity();
    viewForm();
  };

  const viewForm = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    fetchEntities();
  };

  return (
    <>
      <Box
        gap={2}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "15px 30px",
          borderBottom: "1px solid var(--color-primary-200)",
        }}
      >
        <Header title={`${formName}s`} />
        <Box sx={{ borderLeft: "1px solid var(--color-primary-200)" }}>
          <Button
            type="submit"
            variant="text"
            onClick={openCreateForm}
            sx={{ padding: "5px 15px" }}
            startIcon={<AddIcon />}
          >
            {`Create ${formName}`}
          </Button>
        </Box>
      </Box>

      <Box m="30px" display="flex" flexDirection="column" flex={1}>
        <Box
          sx={{
            height: {
              xs: "100%",
              sm: 300,
              md: "calc(100vh - 65px - 50px - 60px)",
            },
          }}
        >
          <DataGrid
            rows={entitiesList}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            columnHeaderHeight={isSmallScreen ? 70 : 60}
            sx={{
              "& .MuiDataGrid-columnHeader--moving": {
                backgroundColor: "inherit !important",
              },
              ...(isSmallScreen && {
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-sortIcon": {
                  display: "none",
                },
                "& .MuiDataGrid-menuIcon": {
                  display: "none",
                },
                "& .MuiDataGrid-columnHeader": {
                  padding: "4px !important",
                  justifyContent: "center",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  padding: "0 !important",
                  margin: "0 !important",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "normal !important",
                  wordBreak: "normal",
                  textAlign: "center",
                  lineHeight: 1.2,
                },
              }),
            }}
          />
        </Box>
      </Box>

      <ConfirmationDialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmAction}
      />

      <ProjectModuleForm
        formData={editingEntity}
        projects={projects}
        moduleCategories={moduleCategories}
        open={open}
        handleClose={() => handleClose()}
      />
    </>
  );
};

const formName = "Project Module";
const deletedSuccessMsg = `${formName} deleted successfully: `;
const deleteErrorMsg = `Not deleted ${formName}. `;
const fetchErrorMsg = `Not loading ${formName}s`;

export default ProjectModules;
